/**
 * OneDrive, via Microsoft Graph.
 *
 * Registered as a "Single-page application" in Entra, which is the platform
 * type that allows PKCE with no client secret and still issues refresh tokens.
 * Registering it as "Web" instead would demand a secret at token exchange and
 * make a browser-only page impossible — the single most common way this
 * integration is got wrong.
 *
 * `offline_access` is what asks for the refresh token; Microsoft rolls it on
 * roughly a ninety-day window, so a connection left completely idle for longer
 * than that will need reconnecting.
 */
import type { OAuthApp } from '../oauth';
import type { ProviderMeta, StorageBackend } from './types';
import { CLIENT_IDS } from '../providerConfig';

const APP: OAuthApp = {
  clientId: CLIENT_IDS.onedrive,
  // `common` lets both personal and work accounts sign in.
  authorizeUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
  tokenUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
  scopes: ['Files.ReadWrite.AppFolder', 'offline_access'],
};

export const oneDriveMeta: ProviderMeta = {
  id: 'onedrive',
  name: 'OneDrive',
  blurb: 'Sign in once. Confined to its own app folder.',
  free: '5 GB free',
  auth: 'oauth',
  durability: {
    lasts: 'months',
    note: 'Microsoft renews the connection on a rolling ~90-day window.',
  },
  app: APP,
  needsSetup: !CLIENT_IDS.onedrive,
};

export function oneDriveApp(): OAuthApp {
  return APP;
}

// The special app-folder root. Because the scope is Files.ReadWrite.AppFolder,
// this is the only place we can reach at all — the rest of the drive is not
// merely untouched, it is unreachable.
const ROOT = 'https://graph.microsoft.com/v1.0/me/drive/special/approot';

export function oneDriveBackend(accessToken: string): StorageBackend {
  const auth = { Authorization: `Bearer ${accessToken}` };
  const item = (path: string) => `${ROOT}:/${encodeURIComponent(path)}`;

  return {
    async put(path, body) {
      // Simple upload, good to 4 MB. An envelope is far smaller; anything
      // larger would need an upload session, which is not worth carrying yet.
      const res = await fetch(`${item(path)}:/content`, {
        method: 'PUT',
        headers: { ...auth, 'Content-Type': 'application/octet-stream' },
        body: body as BodyInit,
      });
      if (!res.ok) throw new Error(await describe(res));
      const j = (await res.json()) as { size?: number };
      return { size: j.size ?? body.byteLength };
    },

    async get(path) {
      const res = await fetch(`${item(path)}:/content`, { headers: auth });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(await describe(res));
      return new Uint8Array(await res.arrayBuffer());
    },

    async remove(path) {
      const res = await fetch(item(path), { method: 'DELETE', headers: auth });
      if (!res.ok && res.status !== 404) throw new Error(await describe(res));
    },
  };
}

async function describe(res: Response): Promise<string> {
  const text = await res.text().catch(() => '');
  try {
    const j = JSON.parse(text) as { error?: { message?: string } };
    if (j.error?.message) return `OneDrive: ${j.error.message}`;
  } catch {
    /* not JSON */
  }
  return `OneDrive returned ${res.status}${text ? ` — ${text.slice(0, 120)}` : ''}`;
}
